/**
 * O que a tela diz depois de cada ação, decidido do resultado real (o 200 do
 * servidor e a resposta da serial), nunca suposto.
 */
import type { AjustesDoRobo } from '@/api/robo'
import { codigoDaRecusa } from '@/api/robo'
import type { ResultadoDoAjuste, ResultadoDoReinicio } from './aplicar'
import { duracao } from './canal'

export type Mensagem = { cor: 'green' | 'yellow' | 'red' | 'gray'; texto: string }

const CAMPO: Record<string, string> = {
  brilho: 'brilho', rotacao: 'orientação do rosto', rotacao_info: 'orientação das informações',
}

export function nomesDosCampos(campos: readonly string[]): string {
  const nomes = campos.map((c) => CAMPO[c] ?? c)
  return nomes.length > 2 ? `${nomes.slice(0, -1).join(', ')} e ${nomes[nomes.length - 1]}` : nomes.join(' e ')
}

export function mensagemDoResultado(r: ResultadoDoAjuste, operacao = false): Mensagem {
  switch (r.tipo) {
    case 'aplicado':
      return r.recusado.length
        ? { cor: 'yellow', texto: `Salvo, e o robô recusou ${nomesDosCampos(r.recusado)}: ficou como estava.` }
        : { cor: 'green', texto: 'Aplicado no robô pela USB.' }
    case 'salvo':
      return operacao
        ? { cor: 'green', texto: 'Salvo. Vale a partir da próxima consulta do robô ao Console.' }
        : { cor: 'green', texto: 'Salvo. O robô aplica na próxima consulta, em até 30 s.' }
    case 'salvo_sem_aplicar':
      return { cor: 'yellow', texto: 'Salvo no Console, falta aplicar: o robô não confirmou pela USB em 5 s. Se ele ajusta pela rede, recebe na próxima consulta.' }
    case 'firmware_sem_ajustes':
      return { cor: 'yellow', texto: 'Salvo no Console, falta aplicar: o firmware deste robô não tem ajustes pela USB. Atualize o firmware.' }
    case 'recusado_pela_placa':
      return { cor: 'yellow', texto: 'Salvo no Console, falta aplicar: o robô recusou a linha de ajuste.' }
  }
}

export function mensagemDoReinicio(r: ResultadoDoReinicio): Mensagem {
  switch (r) {
    case 'reiniciando': return { cor: 'green', texto: 'O robô respondeu e está reiniciando. O tempo ligado volta a zero no diagnóstico.' }
    case 'pedido': return { cor: 'green', texto: 'Pedido registrado. O robô reinicia na próxima consulta, em até 30 s.' }
    case 'sem_resposta': return { cor: 'yellow', texto: 'Pedido registrado, mas o robô não respondeu pela USB em 5 s.' }
    case 'firmware_sem_ajustes': return { cor: 'yellow', texto: 'Pedido registrado, mas o firmware deste robô não tem o reinício pela USB. Atualize o firmware.' }
  }
}

const RECUSA: Record<string, string> = {
  sem_mudanca: 'Nada mudou: o Console já tinha esses valores.',
  robo_nao_ativo: 'Só robô ativo recebe ajuste. Reative o robô antes.',
  campo_invalido: 'O Console recusou um dos valores.',
}

export function mensagemDoErro(erro: unknown): Mensagem {
  const codigo = codigoDaRecusa(erro)
  if (codigo && RECUSA[codigo]) {
    return { cor: codigo === 'sem_mudanca' ? 'gray' : 'red', texto: RECUSA[codigo] }
  }
  return { cor: 'red', texto: 'O Console não salvou o ajuste. Nada foi mandado ao robô.' }
}

/**
 * Onde está a Tela: no padrão, aplicada, pendente ou salva sem como aplicar.
 * `aplicadoPelaUsb`: a versão que o robô confirmou nesta tela, pela serial.
 * `salvoEm`: quando esta tela salvou a versão atual (só então há o "há N s").
 */
export function situacaoDaTela(
  a: AjustesDoRobo, aplicadoPelaUsb: number | null, salvoEm: number | null, agora: number,
): Mensagem {
  const versao = a.desejado.versao
  if (versao === 0) return { cor: 'gray', texto: 'Padrão de fábrica: nada foi pedido a este robô.' }
  if (a.informado === versao || aplicadoPelaUsb === versao) {
    return a.recusado.length && a.informado === versao
      ? { cor: 'yellow', texto: `Aplicado, menos ${nomesDosCampos(a.recusado)}, que o robô recusou.` }
      : { cor: 'green', texto: 'Aplicado no robô.' }
  }
  if (a.capaz) {
    const ha = salvoEm !== null ? ` há ${duracao((agora - salvoEm) / 1000)}` : ''
    return { cor: 'yellow', texto: `Pendente${ha}: o robô ainda não informou a versão ${versao}.` }
  }
  return { cor: 'yellow', texto: 'Salvo, falta aplicar: o robô não ajusta pela rede. Conecte pela USB e aplique de novo.' }
}
